import { Component, OnDestroy, OnInit, ViewChild, inject, signal } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSort, MatSortHeader } from '@angular/material/sort';
import {
  MatTableDataSource,
  MatTable,
  MatColumnDef,
  MatHeaderCellDef,
  MatHeaderCell,
  MatCellDef,
  MatCell,
  MatHeaderRowDef,
  MatHeaderRow,
  MatRowDef,
  MatRow,
} from '@angular/material/table';
import { Subject, first, takeUntil } from 'rxjs';
import {
  CountryProduct,
  CountryProductService,
} from 'src/app/services/country-product/country-product.service';
import {
  Product,
  ProductService,
} from 'src/app/services/product-service/product-service.service';
import { DeleteProductDialogComponent } from './delete-product-dialog/delete-product-dialog.component';
import { EditProductDialogComponent } from './edit-product-dialog/edit-product-dialog.component';
import { PayeeFormFieldComponent } from '../../../shared/payee-form-fields/payee-form-field/payee-form-field.component';
import { PayeeAutocompleteComponent } from '../../../shared/payee-form-fields/payee-autocomplete/payee-autocomplete.component';
import { PayeeButtonComponent } from '../../../shared/payee-buttons/payee-button/payee-button.component';
import { PayeeToggleComponent } from '../../../shared/payee-toggle/payee-toggle.component';
import { PayeeIconButtonComponent } from '../../../shared/payee-buttons/payee-icon-button/payee-icon-button.component';
import { PayeeIconComponent } from '../../../shared/payee-icon/payee-icon.component';
import { MatProgressSpinner } from '@angular/material/progress-spinner';

@Component({
  selector: 'app-product-prices',
  templateUrl: './product-prices.component.html',
  styleUrls: ['./product-prices.component.scss'],
  imports: [
    PayeeFormFieldComponent,
    PayeeAutocompleteComponent,
    FormsModule,
    ReactiveFormsModule,
    PayeeButtonComponent,
    MatTable,
    MatSort,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatSortHeader,
    MatCellDef,
    MatCell,
    PayeeToggleComponent,
    PayeeIconButtonComponent,
    PayeeIconComponent,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    MatProgressSpinner,
  ],
})
export class ProductPricesComponent implements OnInit, OnDestroy {
  private productService = inject(ProductService);
  private countryProductService = inject(CountryProductService);
  private dialog = inject(MatDialog);

  private readonly destroy = new Subject<void>();

  readonly dataSource = new MatTableDataSource<Product>();
  readonly displayedColumns = [
    'name',
    'type',
    'debtor_country_iso',
    'client_country_iso',
    'law_firms',
    'activation',
    'edit',
    'delete',
  ];
  readonly loading = signal(false);

  countryOptions: CountryProduct[] = [];
  clientCountryControl = new FormControl<CountryProduct | null>(null);
  debtorCountryControl = new FormControl<CountryProduct | null>(null);

  @ViewChild(MatSort) set sort(sort: MatSort) {
    this.dataSource.sort = sort;
  }

  async ngOnInit(): Promise<void> {
    const [countries] = await Promise.all([
      this.countryProductService.getCountries(),
    ]);

    this.countryOptions = countries;
    await this.setProducts();
  }

  async applyFilters(): Promise<void> {
    await this.setProducts();
  }

  ngOnDestroy(): void {
    this.destroy.next();
    this.destroy.complete();
  }

  editProduct(product?: Product): void {
    const ref = this.dialog.open(EditProductDialogComponent, {
      data: {
        product,
        countryOptions: this.countryOptions,
      },
    });
    ref
      .afterClosed()
      .pipe(first(), takeUntil(this.destroy))
      .subscribe({
        next: async (result?: Product) => {
          if (result) {
            if (product) {
              Object.assign(product, result);
            } else {
              this.dataSource.data = this.dataSource.data.concat(result);
            }
          }
        },
      });
  }

  deleteProduct(product: Product): void {
    const ref = this.dialog.open(DeleteProductDialogComponent, {
      data: product,
    });

    ref
      .afterClosed()
      .pipe(first(), takeUntil(this.destroy))
      .subscribe({
        next: async (result?: boolean) => {
          if (result) {
            const index = this.dataSource.data.findIndex(
              (p) => p.id === product.id,
            );
            this.dataSource.data.splice(index, 1);
            this.dataSource.data = [...this.dataSource.data];
          }
        },
      });
  }

  countryDisplayWith(country?: CountryProduct): string {
    return country?.name ?? '';
  }

  /** Display country ISOs with cutoff; shows first maxVisible then "+N" for the rest. */
  getCountryIsosDisplay(
    isos: string[] | null | undefined,
    maxVisible = 3,
  ): string {
    const arr = isos == null ? [] : Array.isArray(isos) ? isos : [isos];
    if (arr.length === 0) return '—';
    const visible = arr.slice(0, maxVisible);
    const rest = arr.length - maxVisible;
    if (rest <= 0) return visible.join(', ');
    return visible.join(', ') + ' (+' + rest + ')';
  }

  async setProductIsActive(toggled: boolean, product: Product): Promise<void> {
    try {
      if (toggled) {
        await this.productService.activateProduct(product);
      } else {
        await this.productService.deactivateProduct(product);
      }
      product.is_active = toggled;
    } catch (error) {
      console.error('Error while changing is active of product', error);
    }
  }

  private async setProducts(): Promise<void> {
    if (this.loading()) {
      return;
    }

    try {
      this.loading.set(true);

      const client = this.clientCountryControl.value;
      const debtor = this.debtorCountryControl.value;
      const result = await this.productService.getProducts(
        client && debtor
          ? { client_country_iso: client.iso, debtor_country_iso: debtor.iso }
          : undefined,
      );
      this.dataSource.data = result.products;
    } catch (error) {
      console.error('Error while getting products', error);
    } finally {
      this.loading.set(false);
    }
  }
}
